/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import SweetAlert from 'react-bootstrap-sweetalert';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
// formik
import { Formik } from 'formik';
import * as Yup from 'yup';
// @material-ui icons
import Check from '@material-ui/icons/Check';
// @material-ui/core components
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import withStyles from '@material-ui/core/styles/withStyles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
// core components
import Card from 'components/UI/Card/Card';
import GridItem from 'components/UI/Grid/GridItem';
import CardBody from 'components/UI/Card/CardBody';
import Snackbars from 'components/UI/Snackbar/Snackbar';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
// our components
import adCreateStyles from 'assets/jss/AdsCreate';
import { PAID_AD } from 'routes/paths';
import RouterContext from 'helpers/routerContext';
import InfoUser from 'components/common/InfoUser';
import Loading from 'components/common/Loading';
import UserSelect from 'components/User/Select';
import Datepicker from 'components/common/DatePicker';
import Moment from 'react-moment';
import AuthContext from 'helpers/authContext';
import WithPermission from 'components/User/WithPermission';
import { leads, userRanks, paidAdCurrency } from 'config';
import FormControl from '@material-ui/core/FormControl';
import { allowPaidAdChangeCurrency } from 'helpers/functions';

/**
 * @description
 * Validation form's rules.
 * @param {Function} t - Translation function from HOC.
 * @returns {Object} - YUP shape.
 */

const translateValidationSchema = t =>
  Yup.object({
    ad: Yup.string().required(t('errors.required')),
    fromOwner: Yup.boolean(),
    owner: Yup.object()
      .nullable()
      .when('fromOwner', {
        is: false,
        then: Yup.object()
          .nullable()
          .required(t('errors.required')),
      }),
    pack: Yup.object(),
    free: Yup.bool(),
  });

const ReadableDate = ({ years, months, days, hours, minutes, seconds }) =>
  moment()
    .add({ seconds, minutes, hours, days, months, years })
    .fromNow(true);

class PaidAdUpdate extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);

    const { ad: paidAdData } = props;
    const configurationPaidAd = props.packs.find(
      pack =>
        ReadableDate(pack.frequency) === ReadableDate(paidAdData.frequency),
    ).configuration;

    this.state = {
      fields: {
        ad: paidAdData.ad.id,
        date: moment(paidAdData.startDate),
        pack: {
          frequency: paidAdData.frequency,
          configuration: configurationPaidAd,
        },
        agency: paidAdData.manager,
        owner: paidAdData.owner,
        fromOwner: paidAdData.manager === null,
        free: paidAdData.netSale === 0,
        lead: paidAdData.lead,
        currency: paidAdData.currency,
        secondaryCurrency: paidAdData.secondaryCurrency,
      },
      showSelectAgency: false,
      showSelectOwner: false,
      pickedSetup: {
        duration: paidAdData.duration,
        schedule: paidAdData.schedule,
        price: paidAdData.userPrice,
      },
      createdFromManager: paidAdData.manager !== null,
      exchangeRate: null,
    };
    this.formikRef = null;
    moment.locale(props.i18n.language);
  }

  /**
   * @description
   * Handle redirect of cancel btn.
   */
  handleCancelBtn = () => {
    const history = this.context;
    history.push(PAID_AD.LIST);
  };

  /**
   * @description
   * Updating before send to parent.
   *
   * @param {*} values - New values to update fields.
   * @returns {*} Undefined - No returns.
   */
  handleSubmit = values => {
    const { onSave, ad } = this.props;
    const { pickedSetup } = this.state;
    const { __typename: typename, ...frequency } = values.pack.frequency;
    const { __typename: typename1, ...duration } = pickedSetup.duration;

    const finalValues = {
      id: ad.id,
      ad: values.ad,
      frequency,
      schedule: pickedSetup.schedule,
      duration,
      owner: values.owner ? values.owner.id : undefined,
      free: values.free,
      startDate: values.date.format() || null,
      secondaryCurrency: values.secondaryCurrency,
    };
    if (ad.online)
      return onSave({ id: finalValues.id, ad: finalValues.ad }, 'online');
    if (ad.manager)
      return onSave({ ...finalValues, lead: values.lead }, 'fromAgency');
    return onSave(finalValues, 'fromOwner');
  };

  /**
   * @description
   * Set server errors with setErrors function of Formik.
   *
   * @param {*} serverErrors - Api errors.
   */
  setServerErrors = serverErrors => {
    let all = null;
    if (this.formikRef && serverErrors.length) {
      const errors = {};

      serverErrors.forEach(item => {
        errors[item.field] = item.messages;
        if (item.field === '__all__') {
          all = item.messages;
        }
      });

      setTimeout(() => this.formikRef && this.formikRef.setErrors(errors), 0);
    }
    return all;
  };

  render() {
    const {
      classes,
      errors: serverErrors,
      submitLoading,
      t,
      packs,
      exchangeRates,
      ad: paidAdData,
    } = this.props;
    const originalSecondaryCurrency = paidAdData.secondaryCurrency;
    const originalExchangeRate =
      paidAdData.userPrice > 0 && paidAdData.secondaryUserPrice > 0
        ? paidAdData.secondaryUserPrice / paidAdData.userPrice
        : null;
    const {
      showSelectOwner,
      showSelectAgency,
      pickedSetup,
      createdFromManager,
      fields,
      exchangeRate,
    } = this.state;

    const all = this.setServerErrors(serverErrors);
    const validationSchema = translateValidationSchema(t);

    // if the paid ad was created Online  or
    // is running (the lastRenewal has value) we can edit just the ID
    const isEditionForbidden =
      paidAdData.online ||
      paidAdData.lastRenewals !== null ||
      paidAdData.hasPromotion;
    return (
      <AuthContext.Consumer>
        {/* eslint-disable-next-line consistent-return */}
        {user => {
          const hasGeneral =
            user.allPermissions.filter(
              permission => permission.codename === 'change_autopaidad',
            ).length === 1;
          return (
            <React.Fragment>
              <Snackbars
                place="tc"
                message={all || ''}
                open={!!all}
                color="danger"
                close
              />

              <Formik
                ref={ref => {
                  this.formikRef = ref;
                }}
                initialValues={fields}
                validationSchema={validationSchema}
                onSubmit={values => this.handleSubmit(values)}
                render={({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleSubmit,
                  handleBlur,
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div className={classes.pageContainer}>
                      <div
                        className={`${classes.row} ${classes.contentBtns}`}
                        style={{ justifyContent: 'space-between' }}
                      >
                        <h3>{t('paidAd.form.edit')}</h3>
                      </div>

                      <Card>
                        <CardBody>
                          <Loading show={submitLoading} />
                          <GridContainer>
                            <GridItem xs={12} sm={12}>
                              <div style={{ marginBottom: 20 }}>
                                <TextField
                                  label={t('paidAd.form.fields.id')}
                                  name="ad"
                                  error={touched.ad && !!errors.ad}
                                  helperText={touched.ad ? errors.ad : ''}
                                  InputProps={{
                                    value: values.ad,
                                    onChange: e => {
                                      handleChange(e);
                                    },
                                    onBlur: handleBlur,
                                    type: 'number',
                                  }}
                                />
                              </div>
                            </GridItem>
                            <GridItem xs={12} sm={6}>
                              {isEditionForbidden ? (
                                <React.Fragment>
                                  <h6>{t('paidAd.form.fields.date')}</h6>
                                  <Moment format="LLLL" date={values.date} />
                                </React.Fragment>
                              ) : (
                                <Datepicker
                                  title={t('paidAd.form.fields.date')}
                                  defaultValue=""
                                  required
                                  fromToday
                                  dateTime
                                  onChange={date => {
                                    handleChange({
                                      target: {
                                        name: 'date',
                                        value: date || '',
                                      },
                                    });
                                  }}
                                  value={values.date}
                                />
                              )}
                            </GridItem>
                            <GridItem xs={12} sm={6}>
                              <h6>{t('paidAd.form.end_date')}</h6>
                              <Moment format="LLLL" date={paidAdData.endDate} />
                            </GridItem>
                          </GridContainer>
                          {createdFromManager &&
                            (hasGeneral || user.rank === userRanks.ADMIN) && (
                              <FormControl
                                fullWidth
                                className={classes.userSelect}
                              >
                                <h6> {t('userAgency.lead')} </h6>
                                <GridContainer>
                                  <GridItem xs={12} sm={6}>
                                    <Button
                                      data-cy="sellerLead"
                                      id="seller-lead"
                                      name="sellerLead"
                                      simple={values.lead !== leads.SELLER}
                                      round
                                      fullWidth
                                      color="info"
                                      onClick={() => {
                                        handleChange({
                                          target: {
                                            name: 'lead',
                                            value: leads.SELLER,
                                          },
                                        });
                                      }}
                                    >
                                      Seller
                                    </Button>
                                  </GridItem>
                                  <GridItem xs={12} sm={6}>
                                    {' '}
                                    <Button
                                      data-cy="revoLead"
                                      id="revo-lead"
                                      name="revoLead"
                                      simple={values.lead !== leads.REVOLICO}
                                      round
                                      fullWidth
                                      color="info"
                                      onClick={() => {
                                        handleChange({
                                          target: {
                                            name: 'lead',
                                            value: leads.REVOLICO,
                                          },
                                        });
                                      }}
                                    >
                                      Revolico
                                    </Button>
                                  </GridItem>
                                </GridContainer>
                              </FormControl>
                            )}
                          {/* frequency */}
                          <div className={classes.section}>
                            <h6
                              style={{
                                color:
                                  touched.pack && errors.pack ? 'red' : 'black',
                              }}
                            >
                              {t('paidAd.form.fields.frequency')}
                            </h6>

                            {packs.map((pack, key) => (
                              <Button
                                key={`frequency-${pack.configuration[0].price}`}
                                simple={
                                  values.pack
                                    ? ReadableDate(pack.frequency) !==
                                      ReadableDate(values.pack.frequency)
                                    : true
                                }
                                round
                                id={`frequency-${key}`}
                                color="info"
                                onClick={() => {
                                  handleChange({
                                    target: {
                                      name: 'pack',
                                      value: pack,
                                    },
                                  });
                                  this.setState({
                                    pickedSetup: pack.configuration[0],
                                  });
                                }}
                                disabled={isEditionForbidden}
                              >
                                {ReadableDate(pack.frequency)}
                              </Button>
                            ))}

                            {pickedSetup && (
                              <p>
                                {t(
                                  `paidAd.form.fields.schedule.${pickedSetup.schedule}`,
                                )}
                              </p>
                            )}
                            {touched.pack && errors.pack && (
                              <p className={classes.errorCategoryField}>
                                {t('errors.required')}
                              </p>
                            )}
                          </div>
                          {values.pack &&
                          values.pack.configuration.length > 1 ? (
                            <div className={classes.section}>
                              <h6>{t('paidAd.form.fields.duration')}</h6>

                              {values.pack.configuration.map(setup => (
                                <Button
                                  key={`duration-${ReadableDate(
                                    setup.duration,
                                  )}`}
                                  simple={
                                    ReadableDate(pickedSetup.duration) !==
                                    ReadableDate(setup.duration)
                                  }
                                  round
                                  color="info"
                                  onClick={() => {
                                    this.setState({
                                      pickedSetup: setup,
                                    });
                                  }}
                                  disabled={isEditionForbidden}
                                >
                                  {ReadableDate(setup.duration)}
                                </Button>
                              ))}
                            </div>
                          ) : (
                            values.pack && (
                              <div className={classes.section}>
                                <h6>{t('paidAd.form.fields.duration')}</h6>
                                <p>
                                  {ReadableDate(
                                    values.pack.configuration[0].duration,
                                  )}
                                </p>
                              </div>
                            )
                          )}
                          {pickedSetup && (
                            <div className={classes.section}>
                              <div>
                                <WithPermission
                                  allowPermissionFunc={
                                    allowPaidAdChangeCurrency
                                  }
                                >
                                  <div className={classes.section}>
                                    <h6
                                      style={{
                                        color:
                                          touched.pack && errors.pack
                                            ? 'red'
                                            : 'black',
                                      }}
                                    >
                                      {t('paidAd.form.fields.currency')}
                                    </h6>
                                    {Object.keys(paidAdCurrency).map(
                                      (currency, key) => {
                                        const rate = exchangeRates.find(
                                          value =>
                                            value.currencyFrom ===
                                              fields.currency &&
                                            value.currencyTo === currency,
                                        );
                                        if (
                                          fields.currency !== currency &&
                                          !rate
                                        )
                                          return null;

                                        return (
                                          <Button
                                            key={`currency-${currency}`}
                                            simple={
                                              fields.secondaryCurrency
                                                ? fields.secondaryCurrency !==
                                                  currency
                                                : fields.currency !== currency
                                            }
                                            round
                                            id={`currency-${key}`}
                                            color="info"
                                            onClick={() => {
                                              handleChange({
                                                target: {
                                                  name: 'secondaryCurrency',
                                                  value:
                                                    fields.currency !==
                                                      currency && currency,
                                                },
                                              });
                                              this.setState({
                                                fields: {
                                                  ...fields,
                                                  secondaryCurrency:
                                                    fields.currency !==
                                                      currency && currency,
                                                },
                                                exchangeRate:
                                                  originalSecondaryCurrency ===
                                                  currency
                                                    ? originalExchangeRate
                                                    : fields.currency !==
                                                        currency && rate.rate,
                                              });
                                            }}
                                            disabled={isEditionForbidden}
                                          >
                                            {currency}
                                          </Button>
                                        );
                                      },
                                    )}
                                    {touched.pack && errors.pack && (
                                      <p className={classes.errorCategoryField}>
                                        {t('errors.required')}
                                      </p>
                                    )}
                                  </div>
                                </WithPermission>
                              </div>
                              <div>
                                <h6>{t('paidAd.form.fields.price')}</h6>
                                {values.free ||
                                (values.promotionAds &&
                                  values.promotionAds.length === 4) ? (
                                  <p>
                                    {`0 ${values.secondaryCurrency ||
                                      values.currency}`}
                                  </p>
                                ) : (
                                  <p>
                                    {!values.secondaryCurrency &&
                                      `${pickedSetup.price} ${values.currency}`}
                                    {values.secondaryCurrency &&
                                      values.secondaryCurrency ===
                                        originalSecondaryCurrency &&
                                      `${pickedSetup.price *
                                        originalExchangeRate} ${
                                        values.secondaryCurrency
                                      }`}
                                    {values.secondaryCurrency &&
                                      values.secondaryCurrency !==
                                        originalSecondaryCurrency &&
                                      exchangeRate &&
                                      `${pickedSetup.price * exchangeRate} ${
                                        values.secondaryCurrency
                                      }`}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                          {createdFromManager &&
                            !values.fromOwner &&
                            (hasGeneral || user.rank === userRanks.ADMIN) && (
                              <SweetAlert
                                title=""
                                showConfirm={false}
                                showCancel={false}
                                onConfirm={() => {}}
                                customClass={classes.adModalConfirmation}
                                show={showSelectAgency}
                                closeOnClickOutside
                                onCancel={() =>
                                  this.setState({
                                    showSelectAgency: !showSelectAgency,
                                  })
                                }
                              >
                                <UserSelect
                                  listAgencies
                                  selectUser={agency => {
                                    handleChange({
                                      target: {
                                        name: 'agency',
                                        value: agency,
                                      },
                                    });
                                    // reset user owner
                                    if (
                                      !values.agency ||
                                      (values.agency &&
                                        agency.id !== values.agency.id)
                                    ) {
                                      handleChange({
                                        target: {
                                          name: 'owner',
                                          value: null,
                                        },
                                      });
                                    }
                                    this.setState({
                                      showSelectAgency: !showSelectAgency,
                                    });
                                  }}
                                />
                              </SweetAlert>
                            )}
                          <React.Fragment>
                            <div className={classes.section}>
                              {values.agency && !values.fromOwner && (
                                <h6>{t('paidAd.form.fields.agency')}</h6>
                              )}
                              <div className={classes.categorySection}>
                                {values.agency && !values.fromOwner && (
                                  <InfoUser user={values.agency} />
                                )}
                                {!isEditionForbidden &&
                                  !values.fromOwner &&
                                  createdFromManager &&
                                  (hasGeneral ||
                                    user.rank === userRanks.ADMIN) && (
                                    <React.Fragment>
                                      <Button
                                        simple
                                        color="primary"
                                        onClick={() =>
                                          this.setState({
                                            showSelectAgency: true,
                                          })
                                        }
                                      >
                                        {values.agency
                                          ? t('ad.form.fields.change')
                                          : t('ad.form.fields.select')}
                                      </Button>
                                      {isEditionForbidden &&
                                        !values.fromOwner &&
                                        values.agency && (
                                          <Button
                                            simple
                                            color="primary"
                                            onClick={() => {
                                              handleChange({
                                                target: {
                                                  name: 'agency',
                                                  value: null,
                                                },
                                              });
                                              handleChange({
                                                target: {
                                                  name: 'owner',
                                                  value: null,
                                                },
                                              });
                                            }}
                                          >
                                            {t('paidAd.form.clean')}
                                          </Button>
                                        )}
                                    </React.Fragment>
                                  )}
                              </div>
                            </div>
                          </React.Fragment>
                          {!isEditionForbidden && !values.fromOwner && (
                            <SweetAlert
                              title=""
                              showConfirm={false}
                              showCancel={false}
                              onConfirm={() => {}}
                              customClass={classes.adModalConfirmation}
                              show={showSelectOwner}
                              closeOnClickOutside
                              onCancel={() =>
                                this.setState({
                                  showSelectOwner: !showSelectOwner,
                                })
                              }
                            >
                              {values.agency ? (
                                <UserSelect
                                  agency={values.agency}
                                  selectUser={owner => {
                                    handleChange({
                                      target: {
                                        name: 'owner',
                                        value: owner,
                                      },
                                    });
                                    this.setState({
                                      showSelectOwner: !showSelectOwner,
                                    });
                                  }}
                                />
                              ) : (
                                <UserSelect
                                  listUserAgencies
                                  selectUser={owner => {
                                    handleChange({
                                      target: {
                                        name: 'owner',
                                        value: owner,
                                      },
                                    });
                                    this.setState({
                                      showSelectOwner: !showSelectOwner,
                                    });
                                  }}
                                />
                              )}
                            </SweetAlert>
                          )}
                          <React.Fragment>
                            <div className={classes.section}>
                              <h6
                                style={{
                                  color:
                                    touched.owner && errors.owner
                                      ? 'red'
                                      : 'black',
                                }}
                              >
                                {t('paidAd.form.fields.owner')}
                              </h6>
                              <div className={classes.row}>
                                {values.owner && (
                                  <InfoUser user={values.owner} />
                                )}
                                {!isEditionForbidden && !values.fromOwner && (
                                  <React.Fragment>
                                    {values.owner &&
                                      ((!createdFromManager && hasGeneral) ||
                                        createdFromManager ||
                                        user.rank === userRanks.ADMIN) && (
                                        <Button
                                          simple
                                          color="primary"
                                          id="selectOwner"
                                          onClick={() =>
                                            this.setState({
                                              showSelectOwner: true,
                                            })
                                          }
                                        >
                                          {t('ad.form.fields.change')}
                                        </Button>
                                      )}
                                    {!values.owner && !values.fromOwner && (
                                      <Button
                                        simple
                                        color="primary"
                                        id="selectOwner"
                                        onClick={() =>
                                          this.setState({
                                            showSelectOwner: true,
                                          })
                                        }
                                      >
                                        {t('ad.form.fields.select')}
                                      </Button>
                                    )}
                                    {!isEditionForbidden &&
                                      values.owner &&
                                      !values.fromOwner &&
                                      ((!createdFromManager && hasGeneral) ||
                                        createdFromManager ||
                                        user.rank === userRanks.ADMIN) && (
                                        <Button
                                          simple
                                          color="primary"
                                          onClick={() => {
                                            handleChange({
                                              target: {
                                                name: 'owner',
                                                value: null,
                                              },
                                            });
                                          }}
                                        >
                                          {t('paidAd.form.clean')}
                                        </Button>
                                      )}
                                  </React.Fragment>
                                )}
                                {touched.owner && errors.owner && (
                                  <p className={classes.errorCategoryField}>
                                    {t('errors.required')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </React.Fragment>
                          <WithPermission
                            allowPermissions={['make_self_owner_autopaidad']}
                          >
                            {!isEditionForbidden && !createdFromManager && (
                              <div style={{ marginTop: 15 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      onClick={e => {
                                        handleChange(e);
                                      }}
                                      checked={values.fromOwner}
                                      name="fromOwner"
                                      checkedIcon={
                                        <Check
                                          className={classes.checkedIcon}
                                        />
                                      }
                                      icon={
                                        <Check
                                          className={classes.uncheckedIcon}
                                        />
                                      }
                                      classes={{
                                        checked: classes.checked,
                                      }}
                                      disabled={
                                        isEditionForbidden || createdFromManager
                                      }
                                    />
                                  }
                                  label={t('paidAd.form.fields.fromLoggedUser')}
                                />
                              </div>
                            )}
                          </WithPermission>
                          {!paidAdData.online && (
                            <WithPermission
                              allowPermissions={[
                                'make_free_autopaidad',
                                'make_free_ownership_autopaidad',
                              ]}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    onClick={e => {
                                      handleChange(e);
                                    }}
                                    checked={values.free}
                                    name="free"
                                    id="free"
                                    checkedIcon={
                                      <Check className={classes.checkedIcon} />
                                    }
                                    icon={
                                      <Check
                                        className={classes.uncheckedIcon}
                                      />
                                    }
                                    classes={{
                                      checked: classes.checked,
                                    }}
                                  />
                                }
                                label={t('paidAd.form.fields.free')}
                              />
                            </WithPermission>
                          )}
                          <div className={classes.section}>
                            <GridContainer direction="row-reverse">
                              <GridItem xs={12} sm={6}>
                                <div className={classes.contentBtns}>
                                  <Button
                                    id="update-ad"
                                    color="primary"
                                    type="submit"
                                  >
                                    {t('paidAd.form.button_edit')}
                                  </Button>
                                </div>
                              </GridItem>
                              <GridItem xs={12} sm={6}>
                                <Button
                                  id="cancel-create"
                                  simple
                                  color="danger"
                                  onClick={this.handleCancelBtn}
                                >
                                  {t('common.cancel')}
                                </Button>
                              </GridItem>
                            </GridContainer>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </form>
                )}
              />
            </React.Fragment>
          );
        }}
      </AuthContext.Consumer>
    );
  }
}

/**
 * PaidAdUpdate PropTypes
 * @param classes - styles received from withStyles
 * @param ad - Entity data
 * @param packs - Entity data
 * @param errors - Errors data list
 * @param submitLoading - To change to loading status
 * @param t - prop from HOC with string translations
 * @param i18n - prop from HOC with language information
 * @param exchangeRateQuery - Query for get an ExchangeRate
 * @callback onSave - callback called when user clicks save button
 */

PaidAdUpdate.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.instanceOf(Object).isRequired,
  packs: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  submitLoading: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  ad: PropTypes.instanceOf(Object),
  exchangeRates: PropTypes.instanceOf(Array).isRequired,
};

PaidAdUpdate.defaultProps = {
  ad: {},
  submitLoading: false,
};

// to fix: The withStyles HOC set contextTypes to a children component
// and it have problem with contextType assigned to PermissionList component
// This is a small hack, with intermediary Component to resolved it temporally
const PaidAdUpdateWrapper = props => <PaidAdUpdate {...props} />;

export default withTranslation()(
  withStyles(adCreateStyles)(PaidAdUpdateWrapper),
);
